import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useApp } from '../../context/AppContext';
import { LogEntry } from '../../types';
import { formatLogTimestamp } from '../../utils/stringUtils';
import '../LogsPage.css';

interface LogsViewerProps {
  filterPrefix?: string;
  title: string;
  subtitle?: string;
  stripAllPrefixes?: boolean; // If true, strip all [CHANNEL:*] and [PIPE:*] prefixes
}

const LogsViewer: React.FC<LogsViewerProps> = ({ filterPrefix, title, subtitle, stripAllPrefixes = false }) => {
  const { state, actions, getCurrentStats } = useApp();
  const [isPaused, setIsPaused] = useState(false);
  const [isAutoScroll, setIsAutoScroll] = useState(true);
  const [displayedLogs, setDisplayedLogs] = useState<LogEntry[]>([]);
  const [pendingLogs, setPendingLogs] = useState<LogEntry[]>([]);
  const [forceRefresh, setForceRefresh] = useState(false);
  const logContentRef = useRef<HTMLPreElement>(null);
  const lastLogLinesRef = useRef<LogEntry[]>([]);
  
  // Get log timestamp format from settings (default to 'local')
  const logTimestampFormat = state.settings?.log_timestamp_format || 'local';
  
  // Get all log entries from current stats (filtered view)
  const stats = getCurrentStats();
  const allLogEntries = useMemo(() => 
    stats?.proxy_info?.log_entries || [], 
    [stats?.proxy_info?.log_entries]
  );

  // Filter logs by the provided prefix - keep as LogEntry objects
  const logEntries = useMemo(() => {
    if (!allLogEntries || allLogEntries.length === 0) {
      return [];
    }
    
    // If no filter prefix is provided, return all logs
    if (!filterPrefix) {
      return allLogEntries;
    }
    
    // Filter logs that have the specified prefix in the message
    return allLogEntries.filter((entry: LogEntry) => entry.message.includes(filterPrefix));
  }, [allLogEntries, filterPrefix]);

  // Initialize displayed logs on first load
  useEffect(() => {
    if (displayedLogs.length === 0 && logEntries.length > 0) {
      setDisplayedLogs(logEntries);
      lastLogLinesRef.current = logEntries;
    }
  }, [logEntries, displayedLogs.length]);

  // Handle log updates - store new logs in pending if paused, otherwise display immediately
  useEffect(() => {
    // Skip update if logEntries hasn't actually changed (deep equality check)
    if (lastLogLinesRef.current.length === logEntries.length && 
        lastLogLinesRef.current.every((entry, index) => 
          entry.timestamp === logEntries[index].timestamp && 
          entry.message === logEntries[index].message
        )) {
      return;
    }
    
    lastLogLinesRef.current = logEntries;
    
    if (isPaused && !forceRefresh) {
      // When paused, don't update displayed logs but store the latest logs as pending
      setPendingLogs(logEntries);
    } else {
      // When not paused, or when forcing refresh, update displayed logs and clear any pending logs
      setDisplayedLogs(logEntries);
      setPendingLogs([]);
      if (forceRefresh) {
        setForceRefresh(false);
      }
    }
  }, [logEntries, isPaused, forceRefresh]);

  // Auto-scroll to bottom when new logs arrive
  useEffect(() => {
    if (isAutoScroll && logContentRef.current && !isPaused) {
      logContentRef.current.scrollTop = logContentRef.current.scrollHeight;
    }
  }, [displayedLogs, isAutoScroll, isPaused]);

  const togglePause = () => {
    if (isPaused) {
      // When resuming, show all pending logs
      setDisplayedLogs(pendingLogs.length > 0 ? pendingLogs : logEntries);
      setPendingLogs([]);
    }
    setIsPaused(!isPaused);
  };

  const refreshLogs = () => {
    // Set force refresh to true so that even if paused, we show the latest logs
    setForceRefresh(true);
    actions.loadDashboardStats();
  };

  // Escape HTML to prevent XSS
  const escapeHtml = (text: string): string => {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  };

  // Get color for log level
  const getLevelColor = (level: string): string => {
    switch (level.toUpperCase()) {
      case 'ERROR': return '#ff6b6b';
      case 'WARN': return '#ffa500';
      case 'INFO': return '#4dabf7';
      case 'DEBUG': return '#51cf66';
      case 'TRACE': return '#868e96';
      default: return '#adb5bd';
    }
  };

  // Convert ANSI color codes to HTML spans with colors
  const ansiToHtml = (text: string): string => {
    // ANSI color code mapping to CSS colors
    const ansiColors: Record<string, string> = {
      // Foreground colors
      '30': '#000000',  // black
      '31': '#ff5555',  // red
      '32': '#50fa7b',  // green
      '33': '#f1fa8c',  // yellow
      '34': '#6272a4',  // blue
      '35': '#ff79c6',  // magenta
      '36': '#8be9fd',  // cyan
      '37': '#f8f8f2',  // white
      
      // Bright colors
      '90': '#6272a4',  // bright black (gray)
      '91': '#ff6e6e',  // bright red
      '92': '#69ff94',  // bright green
      '93': '#ffffa5',  // bright yellow
      '94': '#d6acff',  // bright blue
      '95': '#ff92df',  // bright magenta
      '96': '#a4ffff',  // bright cyan
      '97': '#ffffff',  // bright white
    };
    
    // Default color for logs
    let currentColor = '#00ff00';
    let currentStyle = '';
    let result = '';
    
    // Split by ANSI escape sequences
    const escapeChar = '\u001b';
    const ansiRegex = new RegExp(`(${escapeChar}\\[[0-9;]*m)`, 'g');
    const parts = text.split(ansiRegex);
    
    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];
      
      // Check if this is an ANSI escape sequence
      const ansiMatchRegex = new RegExp(`${escapeChar}\\[([0-9;]*)m`);
      const ansiMatch = part.match(ansiMatchRegex);
      
      if (ansiMatch) {
        // Parse the codes
        const codes = ansiMatch[1].split(';').filter(c => c !== '');
        
        for (const code of codes) {
          if (code === '0' || code === '') {
            // Reset all attributes
            currentColor = '#00ff00';
            currentStyle = '';
          } else if (code === '1') {
            // Bold
            currentStyle = 'font-weight: bold;';
          } else if (code === '2') {
            // Dim/faint - make slightly transparent
            currentStyle = 'opacity: 0.6;';
          } else if (code === '22') {
            // Normal intensity
            currentStyle = '';
          } else if (ansiColors[code]) {
            // Color code
            currentColor = ansiColors[code];
          }
        }
      } else if (part) {
        // Regular text - wrap in span with current color
        const escapedText = escapeHtml(part);
        if (currentStyle) {
          result += `<span style="color: ${currentColor}; ${currentStyle}">${escapedText}</span>`;
        } else {
          result += `<span style="color: ${currentColor};">${escapedText}</span>`;
        }
      }
    }
    
    return result || `<span style="color: #00ff00;">${escapeHtml(text)}</span>`;
  };

  const formatLogContent = () => {
    if (displayedLogs.length === 0) {
      return `<span style="color: #888;">No logs available${subtitle ? ` for ${subtitle}` : ''}</span>`;
    }

    // Deduplicate consecutive identical log entries
    const deduplicatedLogs: LogEntry[] = [];
    let lastEntry: LogEntry | null = null;
    
    for (const entry of displayedLogs) {
      if (!lastEntry || entry.timestamp !== lastEntry.timestamp || entry.message !== lastEntry.message) {
        deduplicatedLogs.push(entry);
        lastEntry = entry;
      }
    }

    // Strip filter prefix from logs since we already know the context
    // Or strip all common prefixes if stripAllPrefixes is true
    // Convert log entries to HTML with proper formatting
    return deduplicatedLogs.map((entry: LogEntry) => {
      const timestamp = formatLogTimestamp(entry.timestamp, logTimestampFormat);
      const levelColor = getLevelColor(entry.level);
      
      // Remove the filter prefix or all common prefixes from the message
      let cleanedMessage = entry.message;
      
      if (stripAllPrefixes) {
        // Strip all [CHANNEL:*] and [PIPE:*] prefixes
        cleanedMessage = cleanedMessage
          .replace(/\[CHANNEL:[^\]]+\]\s*/g, '')
          .replace(/\[PIPE:[^\]]+\]\s*/g, '')
          .trim();
      } else if (filterPrefix && entry.message.includes(filterPrefix)) {
        // Remove only the specific filter prefix
        cleanedMessage = entry.message.replace(filterPrefix, '').trim();
      }
      
      // Convert ANSI codes in the message to HTML
      const coloredMessage = ansiToHtml(cleanedMessage);
      
      return `<span style="color: #888">${escapeHtml(timestamp)}</span> <span style="color: ${levelColor}; font-weight: bold">${escapeHtml(entry.level)}</span> ${coloredMessage}`;
    }).join('\n');
  };

  return (
    <div className="card shadow mb-4">
      <div className="card-header py-3 d-flex justify-content-between align-items-center">
        <h6 className="m-0 font-weight-bold text-primary">
          <i className="fas fa-file-alt"></i> {title} {subtitle && `- ${subtitle}`}
        </h6>
        <div className="d-flex align-items-center logs-controls">
          <button 
            className={`btn btn-sm btn-${isPaused ? 'success' : 'warning'} mr-2`}
            onClick={togglePause}
            title={isPaused && pendingLogs.length > displayedLogs.length ? `${pendingLogs.length - displayedLogs.length} new logs available` : ''}
          >
            <i className={`fas fa-${isPaused ? 'play' : 'pause'}`}></i> 
            {isPaused ? (
              pendingLogs.length > displayedLogs.length 
                ? ` Resume (${pendingLogs.length - displayedLogs.length} new)` 
                : ' Resume'
            ) : ' Pause'}
          </button>
          <button 
            className="btn btn-sm btn-primary mr-2"
            onClick={refreshLogs}
          >
            <i className="fas fa-sync-alt"></i> Refresh
          </button>
          <small className="text-muted ml-2">
            {displayedLogs.length} log{displayedLogs.length !== 1 ? 's' : ''}
          </small>
        </div>
      </div>
      <div className="card-body p-0">
        <pre 
          ref={logContentRef}
          className="log-viewer"
          style={{maxHeight: "400px"}}
          dangerouslySetInnerHTML={{ __html: formatLogContent() }}
        />
      </div>
    </div>
  );
};

export default LogsViewer;
